import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const CustomCursor = () => {
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [trail, setTrail] = useState([]);
  const trailRef = useRef([]);

  const springConfig = { damping: 28, stiffness: 300, mass: 0.5 };
  const dotX = useSpring(cursorX, { damping: 40, stiffness: 500, mass: 0.2 });
  const dotY = useSpring(cursorY, { damping: 40, stiffness: 500, mass: 0.2 });
  const ringX = useSpring(cursorX, springConfig);
  const ringY = useSpring(cursorY, springConfig);

  useEffect(() => {
    document.body.style.cursor = 'none';
    const move = (e) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      const now = Date.now();
      trailRef.current = [
        { x: e.clientX, y: e.clientY, t: now },
        ...trailRef.current.filter(p => now - p.t < 400),
      ].slice(0, 10);
      setTrail([...trailRef.current]);
    };
    const down = () => setIsClicking(true);
    const up = () => setIsClicking(false);
    const checkHover = (e) => {
      const el = e.target;
      const hoverable = el.closest('a,button,input,[data-cursor-hover]');
      setIsHovering(!!hoverable);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mousemove', checkHover);
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mousemove', checkHover);
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
    };
  }, []);

  return (
    <>
      {trail.map((point, i) => {
        const age = (trail.length - 1 - i) / trail.length;
        const opacity = (1 - age) * 0.18;
        const size = (1 - age) * 4;
        return (
          <div key={i} className="fixed pointer-events-none" style={{
            left: point.x - size / 2, top: point.y - size / 2,
            width: size, height: size, borderRadius: '50%',
            background: '#4a5a67', opacity, zIndex: 9998, transform: 'translateZ(0)',
          }} />
        );
      })}

      <motion.div className="fixed pointer-events-none" style={{ x: ringX, y: ringY, zIndex: 9999, translateX: '-50%', translateY: '-50%' }}>
        <motion.div
          animate={{
            width: isHovering ? 40 : isClicking ? 20 : 28,
            height: isHovering ? 40 : isClicking ? 20 : 28,
            borderColor: isHovering ? 'rgba(153,104,124,0.8)' : 'rgba(74,90,103,0.6)',
            rotate: isHovering ? 45 : 0,
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 22 }}
          style={{ border: '1px solid rgba(74,90,103,0.6)', borderRadius: isHovering ? '2px' : '50%' }}
        >
          {isHovering && (
            <>
              <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
                className="absolute top-0 left-0 w-1.5 h-1.5"
                style={{ borderTop: '1px solid #99687c', borderLeft: '1px solid #99687c' }} />
              <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
                className="absolute bottom-0 right-0 w-1.5 h-1.5"
                style={{ borderBottom: '1px solid #99687c', borderRight: '1px solid #99687c' }} />
            </>
          )}
        </motion.div>
      </motion.div>

      <motion.div className="fixed pointer-events-none" style={{ x: dotX, y: dotY, zIndex: 10000, translateX: '-50%', translateY: '-50%' }}>
        <motion.div
          animate={{
            width: isClicking ? 6 : isHovering ? 3 : 4,
            height: isClicking ? 6 : isHovering ? 3 : 4,
            background: isHovering ? '#99687c' : '#4a5a67',
            opacity: isClicking ? 1 : 0.9,
          }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          style={{ borderRadius: '50%' }}
        />
      </motion.div>

      {isClicking && (
        <motion.div className="fixed pointer-events-none" style={{ x: dotX, y: dotY, zIndex: 9997, translateX: '-50%', translateY: '-50%' }}>
          <motion.div
            initial={{ width: 0, height: 0, opacity: 0.6 }}
            animate={{ width: 60, height: 60, opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{ border: '1px solid rgba(74,90,103,0.5)', borderRadius: '50%', translateX: '-50%', translateY: '-50%' }}
          />
        </motion.div>
      )}
    </>
  );
};

export default CustomCursor;